/**
 * Campaign page → "Brand slots…": tick several placements, then give them
 * brand slots (and "Sold as" where there's a choice) in one go. All or
 * nothing on the server side.
 */
import { useState } from 'react';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';
import { useBulkSlots } from '../../lib/api/assets';
import { useRates } from '../../lib/api/rateCard';
import { CampaignAsset } from '../../lib/types';

export default function BulkSlotsBar({
  selected,
  onDone,
  onClear,
}: {
  selected: CampaignAsset[];
  onDone: () => void;
  onClear: () => void;
}) {
  const toast = useToast();
  const bulk = useBulkSlots();
  const { data: rates } = useRates();
  const [spots, setSpots] = useState('');
  const [soldAs, setSoldAs] = useState('');

  const types = [...new Set(selected.map((a) => a.asset_type))];
  const choicesFor = (t: string) =>
    (rates ?? []).filter((r) => r.active && (r.asset_type === t || (r.pack_types ?? []).includes(t)));
  // "Sold as" can be chosen when one type is selected and it has a choice
  // (Newsletter, Instagram post); everything else is automatic.
  const oneType = types.length === 1 ? types[0] : null;
  const choices = oneType ? choicesFor(oneType) : [];
  const needsPick = types.some((t) => choicesFor(t).length > 1);
  const unsellable = types.filter((t) => choicesFor(t).length === 0);

  const apply = async (clear = false) => {
    const n = parseInt(spots, 10);
    if (!clear && !(n >= 1)) {
      toast.push('Fill in how many brand slots (1 or more).');
      return;
    }
    const body: { ids: number[]; brand_spots: number | null; sold_as?: number | null } = {
      ids: selected.map((a) => a.id),
      brand_spots: clear ? null : n,
    };
    if (!clear && oneType && choices.length > 1) body.sold_as = soldAs ? Number(soldAs) : null;
    try {
      const res = await bulk.mutateAsync(body);
      toast.push(clear
        ? `Brand slots removed from ${res.updated} placement${res.updated === 1 ? '' : 's'}`
        : `${n} brand slot${n === 1 ? '' : 's'} on ${res.updated} placement${res.updated === 1 ? '' : 's'}`);
      onClear();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { errors?: Record<string, Record<string, string[]>> } } })?.response?.data;
      const first = data?.errors ? Object.values(data.errors)[0] : null;
      toast.push(first ? `Nothing changed: ${Object.values(first).flat().join(' ')}` : 'Could not save — nothing changed.');
    }
  };

  return (
    <div
      className="card"
      style={{
        position: 'sticky', bottom: 12, zIndex: 5, marginTop: 10,
        padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        boxShadow: 'var(--shadow-md)', borderLeft: '3px solid #1e7a3c',
      }}
    >
      <Icon name="user" size={15} />
      <strong style={{ fontSize: 13 }}>
        {selected.length ? `${selected.length} selected` : 'Tick the placements to sell'}
      </strong>
      {selected.length > 0 && (
        <>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ink-3)' }}>
            Brand slots
            <input
              className="input"
              type="number"
              min={1}
              max={99}
              value={spots}
              onChange={(e) => setSpots(e.target.value)}
              style={{ width: 70 }}
              aria-label="Brand slots per placement"
            />
          </label>
          {oneType && choices.length > 1 && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ink-3)' }}>
              Sold as
              <select className="input" value={soldAs} onChange={(e) => setSoldAs(e.target.value)} style={{ width: 'auto' }}>
                <option value="">— pick —</option>
                {choices.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </label>
          )}
          <Button variant="primary" onClick={() => apply(false)} disabled={bulk.isPending}>Apply</Button>
          <button type="button" className="link" style={{ fontSize: 12, color: 'var(--warn)' }} onClick={() => apply(true)} disabled={bulk.isPending}>
            Remove their slots
          </button>
        </>
      )}
      <span style={{ fontSize: 11.5, color: 'var(--ink-4)', flex: '1 1 220px' }}>
        {unsellable.length > 0
          ? `Not sellable yet: ${unsellable.join(', ')} — tick it as Sellable on Placements & prices first.`
          : needsPick && !oneType
            ? 'Newsletters and Instagram posts need a “Sold as” — select them separately to choose it.'
            : 'Use “Select all” above to pick every placement of a kind.'}
      </span>
      <Button variant="ghost" onClick={onDone}>Done</Button>
    </div>
  );
}
